import React from 'react';

import {
  QDrawer,
  QFlex,
  QBox,
  QText,
  QHeading,
  QDivider,
  QIconButton,
  QAlert,
  QButton,
  QIcon,
  QButtonGroup,
  QStack,
  useToastProvider,
  QHeader,
  QTitle,
  QCloseButton,
} from '@qualio/ui-components';
import { ReactComponent as SupplierTypeIcon } from 'assets/Bank.svg';
import { ReactComponent as RiskLevelIcon } from 'assets/Dashboard.svg';
import { useTitleResolver } from 'hooks';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, SettingsBody } from 'v2components';
import { ConfigTypeOptions } from 'v2types';

import { StyledSelect } from '../styles/SupplierPolicy.styles';

interface DataRow {
  title: string;
  id?: string;
}
interface SupplierPolicyDrawerProps {
  view?: string;
  row: DataRow;
  column: any;
  policyConfigIndex: string;
  policyConfig: any;
  documentTypes: ConfigTypeOptions[];
  auditTypes: ConfigTypeOptions[];
  isDrawerOpen: boolean;
  setIsDrawerOpen: any;
  setPolicyConfig: any;
  savePolicy: any;
  loading: boolean;
  setLoading: any;
}
const SupplierPolicyDrawer: React.FC<SupplierPolicyDrawerProps> = ({
  row,
  column,
  documentTypes,
  auditTypes,
  policyConfig,
  isDrawerOpen,
  setIsDrawerOpen,
  setPolicyConfig,
  policyConfigIndex,
  savePolicy,
  loading,
  setLoading,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();

  const { resolveTitle } = useTitleResolver(documentTypes, auditTypes);

  const currentList = (type: string) =>
    policyConfig.linkData
      ? policyConfig.linkData
          ?.filter((l: any) => l.linkType === type)
          ?.map((c: any) => c?.linkData?.value || c?.linkData?.id)
      : [];

  const documentTypeOptions = documentTypes
    ?.filter((documentType) => !currentList('documents').includes(documentType.payload?.id))
    ?.map((documentType) => ({
      label: documentType.payload?.title,
      value: documentType.payload?.id,
    }));
  const auditTypeOptions = auditTypes
    ?.filter((auditType) => !currentList('audits').includes(auditType.payload?.id))
    ?.map((auditType) => ({
      label: auditType.payload?.title,
      value: auditType.payload?.id,
    }));

  if (loading)
    return (
      <SettingsBody isEmptyState={true}>
        <LoadingSpinner />
      </SettingsBody>
    );

  const auditPeriodMessage = (months: number) => {
    return <>{t('supplierPolicy.drawer.frequencyOptions.monthsFromLastAudit', { count: months })}</>;
  };

  const NoTypesFound: React.FC<{ text: string }> = ({ text }) => (
    <>
      <QIcon iconName={'AlertTriangle'} color="yellow.600" /> {t('supplierPolicy.drawer.noTypesFound', { type: text })}
    </>
  );
  return (
    <QDrawer isOpen={isDrawerOpen} data-cy="policy-drawer">
      <QHeader showDivider={false}>
        <QTitle>{t('supplierPolicy.drawer.title')}</QTitle>
        <QCloseButton onClick={() => setIsDrawerOpen(false)} />
      </QHeader>
      <QStack direction="row" mb={4}>
        <QFlex direction="row" bgColor="blue.100" borderRadius={4} w={'100%'} p={2}>
          <QFlex align="center" pr={2}>
            <SupplierTypeIcon />
          </QFlex>
          <QBox>
            <QText fontSize="sm" color={'gray.500'}>
              {t('supplierPolicy.drawer.sections.supplierType')}
            </QText>
            <QText fontSize="sm">{row.title}</QText>
          </QBox>
        </QFlex>
        <QFlex direction="row" bgColor="blue.100" borderRadius={4} w={'100%'} p={2}>
          <QFlex align="center" pr={2}>
            <RiskLevelIcon />
          </QFlex>
          <QBox>
            <QText fontSize="sm" color={'gray.500'}>
              {t('supplierPolicy.drawer.sections.riskLevel')}
            </QText>
            <QText fontSize="sm">{column?.data?.title}</QText>
          </QBox>
        </QFlex>
      </QStack>
      <QDivider />
      <QHeading size="sm" mt={4}>
        {t('supplierPolicy.drawer.sections.requiredDocuments')}
      </QHeading>
      <QText fontSize="sm" color="gray.500">
        {t('supplierPolicy.drawer.descriptions.requiredDocuments')}
      </QText>
      <br />
      {policyConfig?.linkData
        ?.filter((l: any) => l.linkType === 'documents')
        ?.map((config: any, index: number) => (
          <QBox key={index} className="type-list" data-cy="policy-document-list">
            <QText fontSize="sm">
              {resolveTitle(config.linkData.id, 'documents') || config.linkData.label || config.linkData.title}{' '}
              <QIconButton
                size="sm"
                iconName={'Trash2'}
                aria-label="delete"
                onClick={async () => {
                  await setPolicyConfig(row, column.data, policyConfigIndex, undefined, 'documents', 'delete', config);
                }}
              />
            </QText>
          </QBox>
        ))}
      {/* TODO https://qualio.atlassian.net/browse/SUP-1134 Change selects to Component library selects */}
      {/* @ts-expect-error - styled-components v5 has incompatible types with React 18 */}
      <StyledSelect
        aria-label="edit-supplier-policy-document-select"
        options={documentTypeOptions}
        placeholder={t('supplierPolicy.drawer.placeholders.addDocumentType')}
        value={[]}
        menuPlacement="bottom"
        onChange={async (e: any) => {
          await setPolicyConfig(row, column.data, policyConfigIndex, e, 'documents', 'add');
        }}
        noOptionsMessage={() => <NoTypesFound text="Document" />}
      />
      <QDivider />
      <QHeading size="sm" mt={4}>
        {t('supplierPolicy.drawer.sections.audits')}
      </QHeading>
      <QText fontSize="sm" color="gray.500">
        {t('supplierPolicy.drawer.descriptions.audits')}
      </QText>
      <QBox>
        {policyConfig?.linkData
          ?.filter((l: any) => l.linkType === 'audits')
          ?.map((config: any, index: number) => (
            <QBox key={index} className="type-list audits" data-cy="policy-audit-list" mt={4} mb={4}>
              <QFlex direction={'row'} align="center">
                <QText fontSize="sm" mr="2" w="70px">
                  {resolveTitle(config.linkData.id, 'audits') || config.linkData.label || config.linkData.title}{' '}
                </QText>
                <QFlex flex="1" w="100%" ml={2}>
                  <QBox w="100%">
                    {/* @ts-expect-error - styled-components v5 has incompatible types with React 18 */}
                    <StyledSelect
                      aria-label="edit-supplier-policy-frequency-select"
                      placeholder={t('supplierPolicy.drawer.placeholders.dueEvery')}
                      className="due-select"
                      value={[
                        {
                          label: !!config.linkData.periodic_review
                            ? auditPeriodMessage(config.linkData.periodic_review)
                            : t('supplierPolicy.drawer.frequencyOptions.once'),
                          value: config.linkData.periodic_review,
                        },
                      ]}
                      options={[
                        { label: t('supplierPolicy.drawer.frequencyOptions.once'), value: 0 },
                        ...[...Array(120).keys()].map((i: number) => ({
                          label: auditPeriodMessage(i + 1),
                          value: i + 1,
                        })),
                      ]}
                      onChange={async (e: any) => {
                        await setPolicyConfig(row, column.data, policyConfigIndex, e, 'audits', 'update', config);
                      }}
                    />
                  </QBox>
                </QFlex>

                <QIconButton
                  size="sm"
                  iconName={'Trash2'}
                  aria-label="delete"
                  onClick={async () => {
                    await setPolicyConfig(row, column.data, policyConfigIndex, undefined, 'audits', 'delete', config);
                  }}
                />
              </QFlex>
            </QBox>
          ))}
      </QBox>
      <QBox>
        {/* @ts-expect-error - styled-components v5 has incompatible types with React 18 */}
        <StyledSelect
          options={auditTypeOptions}
          aria-label="edit-supplier-policy-audit-type-select"
          placeholder={t('supplierPolicy.drawer.placeholders.addAuditType')}
          value={[]}
          menuPlacement="bottom"
          onChange={async (e: any) => {
            await setPolicyConfig(row, column.data, policyConfigIndex, e, 'audits', 'add');
          }}
          noOptionsMessage={() => <NoTypesFound text="Audit" />}
        />
      </QBox>
      <QDivider />
      <QAlert status="warning" description={t('supplierPolicy.drawer.warning')} />
      <QBox mt={4}>
        <QButtonGroup>
          <QButton
            data-cy={`${row.title}-${column.data.title}-cancel`}
            variant="outline"
            onClick={() => setIsDrawerOpen(false)}
          >
            {t('supplierPolicy.drawer.buttons.cancel')}
          </QButton>
          <QButton
            data-cy={`${row.title}-${column.data.title}-save`}
            onClick={async () => {
              try {
                setLoading(true);
                await savePolicy(policyConfigIndex);
                setLoading(false);
                setIsDrawerOpen(false);
                showToast({
                  status: 'success',
                  description: t('toastMessages.policy.savedDescription'),
                  title: t('toastMessages.policy.saved'),
                });
              } catch (e) {
                console.error(e);
                showToast({
                  status: 'error',
                  description: t('toastMessages.policy.saveFailedDescription'),
                  title: t('toastMessages.policy.saveFailed'),
                });
              }
            }}
          >
            {t('supplierPolicy.drawer.buttons.save')}
          </QButton>
        </QButtonGroup>
      </QBox>
    </QDrawer>
  );
};

export default SupplierPolicyDrawer;
